import * as React from "react";
import styled from "@emotion/styled";
import { Button, FadeIn, LoadingSpinner, Text } from "atoms";
import { ConnectWallet, useWalletAddress } from "features/auth";
import { ApiError, postHugPassInfo, HugPassInfoResponse } from "utils/api";
import { useAsync } from "utils/hooks";
import { web3 } from "utils/mintHelpers";
import { constants } from "config";

function OwnedNft() {
  const walletAddress = useWalletAddress();

  const {
    run: runWeb3,
    isLoading: isLoadingWeb3,
    data: dataWeb3,
    isError: isErrorWeb3,
  } = useAsync<string[], ApiError>();

  const {
    run: runApi,
    isLoading: isLoadingApi,
    data: dataApi,
    isError: isErrorApi,
  } = useAsync<HugPassInfoResponse[], ApiError>();

  const isError = isErrorApi || isErrorWeb3;

  // Get Owned NFT IDs from Alchemy
  const getNftFromAlchemy = async (owner: string): Promise<string[]> => {
    const { contractAddress } = constants;
    if (contractAddress) {
      const nfts = await web3.alchemy.getNfts({
        owner,
        contractAddresses: [contractAddress],
      });
      return nfts.ownedNfts.map((nft) => nft.id.tokenId);
    }
    return [];
  };

  // Get NFT Data from Alchemy
  React.useEffect(() => {
    if (walletAddress && !dataWeb3) {
      runWeb3(getNftFromAlchemy(walletAddress));
    }
  }, [walletAddress, dataWeb3, runWeb3]);

  // Get NFT Data from API
  React.useEffect(() => {
    if (dataWeb3 && dataWeb3.length > 0 && !dataApi) {
      runApi(postHugPassInfo(dataWeb3));
    }
  }, [dataWeb3, dataApi, runApi]);

  // Need Wallet
  if (!walletAddress) {
    return (
      <ConnectWallet>
        <Text size="md">Connect your wallet to view your HUGs</Text>
      </ConnectWallet>
    );
  }

  const retry = () => {
    runWeb3(getNftFromAlchemy(walletAddress));
    if (dataWeb3) {
      runApi(postHugPassInfo(dataWeb3));
    }
  };

  const loading = isLoadingApi || isLoadingWeb3;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <div>
        <Text size="lg">
          <em>
            We were unable to load your NFT.
            <br />
            Please wait a moment and try again.
          </em>
        </Text>
        <Button variant="gradient" type="button" onClick={retry}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <Grid>
      {dataApi?.map(({ description, imageUri, metaDataUri }, index) => (
        <Img
          key={metaDataUri}
          src={imageUri}
          alt={description}
          duration={1500}
          delay={index * 500}
        />
      ))}
    </Grid>
  );
}

const Grid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "1rem",
  "@media (min-width: 800px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

const Img = styled(FadeIn)({
  width: "100%",
}).withComponent("img");

export { OwnedNft };
